import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { 
  Scissors, ArrowLeft, TrendingUp, Users, ShoppingBag,
  Calendar, ChevronLeft, ChevronRight, Filter, CreditCard, Banknote
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import axios from 'axios';
import { branches } from '../data';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = [
  'Leden', 'Únor', 'Březen', 'Duben', 'Květen', 'Červen',
  'Červenec', 'Srpen', 'Září', 'Říjen', 'Listopad', 'Prosinec'
];

const AdminPage = () => {
  const navigate = useNavigate();
  const { currentUser, currentBranch } = useApp();
  
  const currentDate = new Date();
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [selectedBranch, setSelectedBranch] = useState(currentBranch || Object.keys(branches)[0]);
  const [stats, setStats] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Orders state
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [orderFilterBranch, setOrderFilterBranch] = useState('all');
  const [orderFilterStaff, setOrderFilterStaff] = useState('all');
  const [allStaff, setAllStaff] = useState([]);

  const normalizeOrders = (payload) => {
    if (Array.isArray(payload)) {
      return payload.map((order) => ({
        ...order,
        items: Array.isArray(order.items) ? order.items : []
      }));
    }

    if (payload && Array.isArray(payload.value)) {
      return payload.value.map((order) => ({
        ...order,
        items: Array.isArray(order.items) ? order.items : []
      }));
    }

    return [];
  };

  // Redirect if not admin
  useEffect(() => {
    if (!currentUser) {
      navigate('/');
    } else if (currentUser.role !== 'admin') {
      navigate('/pos');
    }
  }, [currentUser, navigate]);

  // keep selectedBranch in sync when user context loads
  useEffect(() => {
    if (currentBranch) setSelectedBranch(currentBranch);
  }, [currentBranch]);

  // Fetch all staff for filters
  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const response = await axios.get(`${API}/staff`);
        setAllStaff(response.data);
      } catch (err) {
        console.error('Failed to fetch staff:', err);
      }
    };
    fetchStaff();
  }, []);

  // Fetch orders with filters
  useEffect(() => {
    const fetchOrders = async () => {
      setOrdersLoading(true);
      try {
        // Add cache busting parameter
        const timestamp = new Date().getTime();
        let url = `${API}/orders?_t=${timestamp}&`;
        if (orderFilterBranch !== 'all') {
          url += `branch=${orderFilterBranch}&`;
        }
        if (orderFilterStaff !== 'all') {
          url += `staff_id=${orderFilterStaff}&`;
        }
        const response = await axios.get(url);
        setOrders(normalizeOrders(response.data));
      } catch (err) {
        console.error('Failed to fetch orders:', err);
        setOrders([]);
      } finally {
        setOrdersLoading(false);
      }
    };
    fetchOrders();
  }, [orderFilterBranch, orderFilterStaff]);

  // Fetch stats for selectedBranch (admins can select other branches)
  useEffect(() => {
    const fetchStats = async () => {
      if (!selectedBranch) return;
      setIsLoading(true);
      try {
          // send the branch slug (e.g. 'smichov') which is stored in the DB
          const response = await axios.get(
            `${API}/stats/${encodeURIComponent(selectedBranch)}/${selectedYear}/${selectedMonth}`
          );
        // Support multiple response shapes: API may return an array or an object with a `value` array
        const raw = Array.isArray(response.data)
          ? response.data
          : (response.data && Array.isArray(response.data.value) ? response.data.value : []);
        // Ensure numeric fields are numbers (backend returns integers, but defensive parse helps)
        const normalized = (raw || []).map((s) => ({
          ...s,
          total_revenue: Number(s.total_revenue) || 0,
          total_tips: Number(s.total_tips) || 0,
          order_count: Number(s.order_count) || 0,
        }));
        setStats(normalized);
      } catch (err) {
        console.error('Failed to fetch stats:', err);
        setStats([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchStats();
  }, [selectedBranch, selectedMonth, selectedYear]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('cs-CZ', {
      style: 'currency',
      currency: 'CZK',
      minimumFractionDigits: 0
    }).format(price);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat('cs-CZ', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const handlePrevMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(prev => prev - 1);
    } else {
      setSelectedMonth(prev => prev - 1);
    }
  };

  const handleNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(prev => prev + 1);
    } else {
      setSelectedMonth(prev => prev + 1);
    }
  };

  const totalRevenue = stats.reduce((sum, s) => sum + s.total_revenue, 0);
  const totalTips = stats.reduce((sum, s) => sum + s.total_tips, 0);
  const totalOrders = stats.reduce((sum, s) => sum + s.order_count, 0);

  const chartData = stats.map(s => ({
    name: s.staff_name,
    revenue: s.total_revenue,
    orders: s.order_count
  }));

  const branchName = (selectedBranch && branches[selectedBranch] && branches[selectedBranch].name) || selectedBranch;

  if (!currentUser || currentUser.role !== 'admin') return null;

  return (
    <div className="min-h-screen bg-obsidian" data-testid="admin-page">
      {/* Header */}
      <header className="bg-paper border-b border-white/10 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/pos')}
            className="text-muted-foreground hover:text-gold"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <Scissors className="w-6 h-6 text-gold" strokeWidth={1.5} />
            <div>
              <h1 className="font-display text-xl font-bold gold-gradient-text">
                STATISTIKY
              </h1>
              <p className="text-xs text-muted-foreground">{branchName}</p>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/my-orders')}
            className="border-gold/50 text-gold hover:bg-gold hover:text-black"
            data-testid="my-orders-btn"
          >
            <ShoppingBag className="w-4 h-4 mr-2" />
            Moje objednávky
          </Button>
          <img
            src={currentUser.avatar}
            alt={currentUser.name}
            className="w-10 h-10 rounded-full border-2 border-gold"
          />
        </div>
      </header>

      {/* Main Content */}
      <div className="p-6 max-w-7xl mx-auto">
        <Tabs defaultValue="stats" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="stats">Statistiky</TabsTrigger>
            <TabsTrigger value="orders">Všechny objednávky</TabsTrigger>
          </TabsList>

          <TabsContent value="stats">
        {/* Month Selector */}
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-2xl font-display font-bold">Měsíční přehled</h2>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-paper rounded-lg p-1 border border-white/10">
              <Button
                variant="ghost"
                size="icon"
                onClick={handlePrevMonth}
                className="hover:text-gold"
                data-testid="prev-month-btn"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>

              <div className="flex items-center gap-2 px-4" data-testid="month-display">
                <Calendar className="w-4 h-4 text-gold" />
                <span className="font-semibold min-w-[120px] text-center">
                  {MONTHS[selectedMonth - 1]} {selectedYear}
                </span>
              </div>

              <Button
                variant="ghost"
                size="icon"
                onClick={handleNextMonth}
                className="hover:text-gold"
                data-testid="next-month-btn"
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>

            {/* Branch selector placed beside the month controls (not inside the rounded control) */}
            {currentUser.role === 'admin' && (
              <div className="ml-2">
                <Select onValueChange={(v) => setSelectedBranch(v)} value={selectedBranch}>
                  <SelectTrigger className="w-44">
                    <SelectValue placeholder="Vyber pobočku" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.keys(branches).map((b) => (
                      <SelectItem key={b} value={b}>{branches[b].name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-paper border-white/10">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground font-normal flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-gold" />
                Celkové tržby
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-mono font-bold text-gold" data-testid="total-revenue">
                {formatPrice(totalRevenue)}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-paper border-white/10">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground font-normal flex items-center gap-2">
                <ShoppingBag className="w-4 h-4 text-gold" />
                Počet objednávek
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-mono font-bold" data-testid="total-orders">
                {totalOrders}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-paper border-white/10">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground font-normal flex items-center gap-2">
                <Users className="w-4 h-4 text-gold" />
                Aktivní holiči
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-mono font-bold" data-testid="active-barbers">
                {stats.filter(s => s.order_count > 0).length} / {stats.length}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-paper border-white/10">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground font-normal flex items-center gap-2">
                <Banknote className="w-4 h-4 text-gold" />
                Dýška celkem
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-mono font-bold text-gold" data-testid="total-tips">
                {formatPrice(totalTips)}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Chart */}
        <Card className="bg-paper border-white/10 mb-8">
          <CardHeader>
            <CardTitle>Tržby podle holičů</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="h-64 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold"></div>
              </div>
            ) : chartData.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                Žádná data pro vybraný měsíc
              </div>
            ) : (
              <div className="h-64" data-testid="revenue-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="name" stroke="#888" />
                    <YAxis stroke="#888" tickFormatter={(value) => `${value / 1000}k`} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#0a0a0a',
                        border: '1px solid #D4AF37',
                        borderRadius: '8px'
                      }}
                      formatter={(value) => [formatPrice(value), 'Tržby']}
                    />
                    <Bar dataKey="revenue" radius={[4, 4, 0, 0]}>
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill="#D4AF37" />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Detailed Stats Table */}
        <Card className="bg-paper border-white/10">
          <CardHeader>
            <CardTitle>Detailní statistiky</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {Array(3).fill(0).map((_, i) => (
                  <div key={i} className="h-16 bg-subtle rounded animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="space-y-3" data-testid="stats-table">
                {stats.map((barber, index) => (
                  <div
                    key={barber.staff_id}
                    className="flex items-center justify-between p-4 bg-subtle rounded-lg"
                    data-testid={`stat-row-${index}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center text-gold font-bold">
                        {barber.staff_name.charAt(0)}
                      </div>
                      <div>
                        <p className="font-semibold">{barber.staff_name}</p>
                        <p className="text-sm text-muted-foreground">
                          {barber.order_count} {barber.order_count === 1 ? 'objednávka' : 
                            barber.order_count >= 2 && barber.order_count <= 4 ? 'objednávky' : 'objednávek'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-mono font-bold text-lg text-gold">
                        {formatPrice(barber.total_revenue)}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Dýška {formatPrice(barber.total_tips || 0)}
                      </p>
                      {barber.order_count > 0 && (
                        <p className="text-sm text-muted-foreground">
                          Ø {formatPrice(Math.round(barber.total_revenue / barber.order_count))}/objednávka
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
          </TabsContent>

          <TabsContent value="orders">
            <div className="space-y-6">
              {/* Filters */}
              <Card className="bg-paper border-white/10">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Filter className="w-5 h-5 text-gold" />
                    Filtry
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-2 block">Pobočka</label>
                      <Select onValueChange={(v) => setOrderFilterBranch(v)} value={orderFilterBranch}>
                        <SelectTrigger>
                          <SelectValue placeholder="Všechny pobočky" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Všechny pobočky</SelectItem>
                          {Object.keys(branches).map((b) => (
                            <SelectItem key={b} value={b}>{branches[b].name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium mb-2 block">Holič</label>
                      <Select onValueChange={(v) => setOrderFilterStaff(v)} value={orderFilterStaff}>
                        <SelectTrigger>
                          <SelectValue placeholder="Všichni holiči" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Všichni holiči</SelectItem>
                          {allStaff.map((s) => (
                            <SelectItem key={s.id} value={s.id}>{s.name} - {branches[s.branch]?.name || s.branch}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Orders List */}
              <Card className="bg-paper border-white/10">
                <CardHeader>
                  <CardTitle>Seznam objednávek</CardTitle>
                </CardHeader>
                <CardContent>
                  {ordersLoading ? (
                    <div className="space-y-3">
                      {Array(5).fill(0).map((_, i) => (
                        <div key={i} className="h-24 bg-subtle rounded animate-pulse" />
                      ))}
                    </div>
                  ) : orders.length === 0 ? (
                    <div className="text-center py-12">
                      <ShoppingBag className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-30" />
                      <p className="text-lg text-muted-foreground">Žádné objednávky nenalezeny</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {orders.map((order) => (
                        <div key={order.id} className="p-4 bg-subtle rounded-lg">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <Calendar className="w-4 h-4 text-gold" />
                                <span className="font-semibold">{formatDate(order.created_at)}</span>
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {order.staff_name} - {branches[order.branch]?.name || order.branch}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-mono font-bold text-lg text-gold mb-1">
                                {formatPrice(order.total)}
                              </div>
                              <div className="text-xs text-muted-foreground mb-1">
                                Dýška {formatPrice(order.tip_amount || 0)}
                              </div>
                              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                {order.payment_method === 'cash' ? (
                                  <>
                                    <Banknote className="w-4 h-4" />
                                    <span>Hotově</span>
                                  </>
                                ) : (
                                  <>
                                    <CreditCard className="w-4 h-4" />
                                    <span>Kartou</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="space-y-1">
                            {order.items && order.items.map((item, idx) => (
                              <div key={idx} className="flex justify-between text-sm">
                                <span className="text-muted-foreground">{item.service_name}</span>
                                <span className="font-mono">{formatPrice(item.price)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPage;
